import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Ban, SearchX } from 'lucide-react';
import { CardDivergenceCancelledItem, CardDivergencePanel } from '@/types/dashboard';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import { financialReportsService } from '@/services/financialReportsService';

/**
 * Painel "Divergências de Recebíveis Cielo" (admin/finance).
 *
 * Com a realocação por TID e a baixa automática, o que chega aqui é só o que
 * precisa de gente: grupos em que a Cielo pagou MENOS que o previsto (âmbar) e
 * parcelas previstas que não aparecem em NENHUM arquivo importado (vermelho —
 * estorno no portal, chargeback, venda que não liquidou). O backend manda
 * contadores completos + top 5 de cada bloco; a ação fica na conciliação.
 *
 * Terceiro bloco (laranja): venda de pedido CANCELADO no ERP que ainda tem
 * parcelas previstas, sem estorno pelo hub. Cancelar o pedido não diz se a
 * Cielo vai pagar — então a decisão é humana e mora aqui: estornar na Cielo
 * (pelo pedido) ou "não vamos receber" (chargeback/cancelamento no portal).
 */
const cents = (v: number): string => formatCurrencyBRL(v / 100);

/** Uma venda de pedido cancelado, com a ação "não vamos receber" em dois cliques. */
const CancelledSaleRow: React.FC<{ item: CardDivergenceCancelledItem; onChanged?: () => void }> = ({
  item,
  onChanged,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const writeOff = async () => {
    setBusy(true);
    setError(null);
    try {
      await financialReportsService.writeOffCancelledOrder(item.credit_card_payment_id);
      setConfirming(false);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao retirar as parcelas da previsão');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="text-sm text-orange-900">
      <div>
        <a
          href={`/sales-orders/${item.order_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline"
        >
          Pedido {item.order_id}
        </a>
        {item.comercial_proposal_id ? ` (Proposta ${item.comercial_proposal_id})` : ''} ·{' '}
        {item.company_name} · {item.brand} · cancelado
        {item.cancelled_at ? ` em ${formatDateBR(item.cancelled_at)}` : ''}
        {item.cancelled_by ? ` por ${item.cancelled_by}` : ''} · {item.installments} parcela(s) prevista(s) de{' '}
        {formatDateBR(item.next_date)} a {formatDateBR(item.last_date)} · {cents(item.net_amount)}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded border border-orange-300 bg-white px-2 py-0.5 text-xs font-medium text-orange-800 hover:bg-orange-100"
          >
            Não vamos receber
          </button>
        ) : (
          <>
            <span className="text-xs">
              Retirar {cents(item.net_amount)} da previsão? Use só se a Cielo já desfez a venda (chargeback ou
              cancelamento no portal).
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={writeOff}
              className="rounded bg-orange-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-60"
            >
              {busy ? 'Retirando…' : 'Confirmar'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="rounded border border-orange-300 bg-white px-2 py-0.5 text-xs text-orange-800 hover:bg-orange-100"
            >
              Cancelar
            </button>
          </>
        )}
        <span className="text-xs text-orange-700">
          ou estorne na Cielo pelo botão “Estornar cartão” do pedido.
        </span>
      </div>
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </li>
  );
};

export const CardDivergencesPanel: React.FC<{ data: CardDivergencePanel; onChanged?: () => void }> = ({
  data,
  onChanged,
}) => (
  <div className="space-y-3">
    {(data.cancelled_count ?? 0) > 0 && (
      <div className="rounded-lg border p-4 bg-orange-50 border-orange-200">
        <div className="flex items-start gap-3">
          <Ban className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-900">
              {data.cancelled_count} venda(s) de pedido cancelado ainda têm parcelas previstas —
              {' '}{cents(data.cancelled_net_cents)} contando na previsão de caixa
            </p>
            <ul className="mt-2 space-y-2">
              {data.cancelled.map((item) => (
                <CancelledSaleRow key={item.credit_card_payment_id} item={item} onChanged={onChanged} />
              ))}
              {data.cancelled_count > data.cancelled.length && (
                <li className="text-sm text-orange-800">
                  … e mais {data.cancelled_count - data.cancelled.length} venda(s).
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    )}

    {data.underpaid_count > 0 && (
      <div className="rounded-lg border p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              Cielo pagou a menos que o previsto em {data.underpaid_count} grupo(s) —
              faltam {cents(-data.underpaid_net_cents)}
            </p>
            <ul className="mt-2 space-y-1">
              {data.underpaid.map((item) => (
                <li key={item.row_id} className="text-sm text-amber-800">
                  {formatDateBR(item.date)} · {item.company_name} · {item.brand_label || item.brand} ·{' '}
                  previsto {cents(item.expected_net)}, pago {cents(item.file_net)}{' '}
                  ({cents(item.diff_net)}) — {item.matched_installments} de {item.expected_installments}{' '}
                  parcela(s) comprovada(s)
                </li>
              ))}
              {data.underpaid_count > data.underpaid.length && (
                <li className="text-sm text-amber-700">
                  … e mais {data.underpaid_count - data.underpaid.length} grupo(s).
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    )}

    {data.missing_count > 0 && (
      <div className="rounded-lg border p-4 bg-red-50 border-red-200">
        <div className="flex items-start gap-3">
          <SearchX className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">
              {data.missing_count} parcela(s) prevista(s) não aparecem em nenhum arquivo da Cielo —
              {' '}{cents(data.missing_net_cents)} sem rastro (estorno, chargeback ou venda que não liquidou)
            </p>
            <ul className="mt-2 space-y-1">
              {data.missing.map((item) => (
                <li key={item.receivable_id} className="text-sm text-red-800">
                  prevista {formatDateBR(item.expected_date)} · {item.company_name} · {item.brand} ·{' '}
                  parcela {item.installment_number}/{item.installments} · {cents(item.net_amount)}
                  {item.order_id
                    ? ` · Pedido ${item.order_id}`
                    : item.comercial_proposal_id
                      ? ` · Proposta ${item.comercial_proposal_id}`
                      : ''}
                </li>
              ))}
              {data.missing_count > data.missing.length && (
                <li className="text-sm text-red-700">
                  … e mais {data.missing_count - data.missing.length} parcela(s).
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    )}

    <Link
      to="/financial/transactions"
      className="inline-block text-sm font-medium text-blue-700 hover:underline"
    >
      Abrir conciliação (Conta Corrente → Conferir planilha da Cielo) →
    </Link>
  </div>
);
